/**
 * Tiny fetch wrapper for the worker REST API.
 *
 * Auth: Cloudflare Access sits in front of the worker and attaches a
 * `CF_Authorization` cookie + `Cf-Access-Jwt-Assertion` header to every
 * request. The browser sends the cookie automatically on same-origin
 * requests, so we just need `credentials: 'include'` everywhere. On 401
 * (typically Access session expired), reload the page — Access will
 * intercept and show its login UI.
 */

/**
 * Cloudflare Access logout URL. Hitting this clears the `CF_Authorization`
 * cookie and bounces the user back to the configured logout target.
 */
export function accessLogoutUrl(): string {
  return '/cdn-cgi/access/logout';
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const raw = await res.text();
  let parsed: unknown = null;
  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = raw;
    }
  }
  if (!res.ok) {
    const msg =
      parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : res.statusText;
    throw new ApiError(res.status, msg, parsed);
  }
  return parsed as T;
}

// ─── Endpoint signatures ──────────────────────────────────────────────────

export interface VideoListItem {
  id: string;
  title: string;
  duration_seconds: number;
  poster_url: string | null;
  bytes: number;
  created_at: number;
}

export interface RoomListItem {
  id: string;
  name: string;
  video_id: string;
  video_title: string | null;
  created_at: number;
  member_count: number;
}

export const api = {
  listVideos: () => call<{ items: VideoListItem[] }>('GET', '/api/videos'),
  createRoom: (name: string, videoId: string) =>
    call<{ id: string }>('POST', '/api/rooms', { name, video_id: videoId }),
  listRooms: () => call<{ items: RoomListItem[] }>('GET', '/api/rooms'),
  getRoom: (id: string) =>
    call<{
      id: string;
      name: string;
      video_id: string;
      video_title: string | null;
      created_at: number;
      closed_at: number | null;
    }>('GET', `/api/rooms/${encodeURIComponent(id)}`),
  closeRoom: (id: string) =>
    call<{ ok: true }>('POST', `/api/rooms/${encodeURIComponent(id)}/close`),
};
